import { uid } from '../../../../lib/uid';

/**
 * A plausible SALN filing, for Autogenerate.
 *
 * **Every value is synthetic and none of it lands in `rsys_pii`.** A SALN is ordinary local
 * data, so this follows `sampleEducation` rather than `samplePersonnel`: the values read as real
 * property descriptions and real creditors, because an unreadable one would make the generated
 * filing worse at the one job it has — exercising the panel, the folder tabs and the totals
 * behind the form.
 *
 * The test is where a value LANDS, not which generator it came from. Nothing here is a person's
 * name, an address anybody lives at, or an account number — a creditor is an institution, and
 * the ones below are generic rather than any bank's actual trading name.
 *
 * **It fills the OPTIONAL fields too.** "The primary action enables" is the floor: a filing
 * generated with only its year produces a panel of four empty folders and three zero totals,
 * which exercises nothing and is indistinguishable from the bug where a schedule was never
 * wired.
 */
export function sampleSaln() {
  const thisYear = new Date().getFullYear();

  /*
    LAST year, not this one. A SALN is filed for a year that has finished, and the server refuses
    a year in the future — generating this year's is legal and generating next year's would not
    be, so the safe and realistic value is the same one.
  */
  const asOfYear = thisYear - 1;

  const tag = uid().slice(0, 6);

  /*
    A UNIQUE SUFFIX on the descriptions, because Autogenerate is most useful clicked repeatedly
    and a reader needs to see that the second click produced different rows. Nothing here is
    unique-indexed, so this is legibility rather than a constraint.
  */
  return {
    asOfYear,

    /*
      JOINT FILING STAYS AT ITS DEFAULT — off. It is a flag the reader decides, and a generator
      that flipped it would make the same button produce a different answer each click. Filling
      what sits behind a flag is the generator's job; deciding the flag is not.
    */
    isJointFiling: false,

    remarks: `Generated filing ${tag} — sample data, not a real declaration.`,

    realProperties: [
      {
        description: `Residential lot ${tag}`,
        exactLocation: 'Barangay Sample, Testville',
        assessedValue: 480_000,
        fairMarketValue: 1_250_000,
        acquisitionYear: asOfYear - 8,
        acquisitionMode: 'Purchase',
        acquisitionCost: 950_000,
      },
      {
        description: `Agricultural land ${tag}`,
        exactLocation: 'Barangay Placeholder, Testville',
        assessedValue: 120_000,
        fairMarketValue: 300_000,
        acquisitionYear: asOfYear - 15,
        acquisitionMode: 'Inheritance',

        /*
          NO ACQUISITION COST on an inherited property, and that is the point of the second row
          rather than an oversight: nothing was paid for it, so the column is genuinely absent
          and the folder's total has to carry a blank without reading as a zero.
        */
        acquisitionCost: null,
      },
    ],

    personalProperties: [
      {
        description: `Motor vehicle ${tag}`,
        yearAcquired: asOfYear - 3,
        acquisitionCost: 860_000,
      },
      {
        description: `Cash on hand and in bank ${tag}`,
        yearAcquired: asOfYear,
        acquisitionCost: 215_000,
      },
    ],

    liabilities: [
      {
        nature: `Housing loan ${tag}`,
        creditorName: 'Government housing fund',
        outstandingBalance: 620_000,
      },
      {
        nature: `Vehicle loan ${tag}`,
        creditorName: 'Sample financing cooperative',
        outstandingBalance: 240_000,
      },
    ],

    businessInterests: [
      {
        entityName: `Sample Trading ${tag}`,
        businessAddress: 'Barangay Testcase, Testville',
        natureOfInterest: 'Sole proprietor',

        /*
          A CALENDAR DATE as `YYYY-MM-DD`, built from parts rather than from an instant. A date
          derived through `toISOString()` is the UTC day, which for a reader west of UTC is the
          day before the one they would have typed.
        */
        acquisitionDate: `${asOfYear - 5}-06-15`,
      },
    ],
  };
}
